/** Lookups derived once per result from the view model (memoised per `ViewModel` object). */
import type { ChannelId } from '../engine/channels/types.ts';
import { CHANNEL_IDS } from '../engine/channels/types.ts';
import type { Hotspot } from '../engine/ensemble/hotspots.ts';
import type { ChannelRow } from '../engine/ensemble/index.ts';
import type { ChannelDetail, ViewModel } from '../shared/viewmodel.ts';

export const channelIndex = (id: ChannelId): number => CHANNEL_IDS.indexOf(id);

export function isChannelId(s: string | undefined): s is ChannelId {
	return s !== undefined && (CHANNEL_IDS as readonly string[]).includes(s);
}

export interface Derived {
	detail: Map<ChannelId, ChannelDetail>;
	row: Map<ChannelId, ChannelRow>;
	/** Hotspot entries per channel (ED-13 channels are absent: they are never listed). */
	hotspotsByChannel: Map<ChannelId, number>;
	/** Paragraph of a character offset. */
	paraOf(pos: number): number;
	withheld: ChannelId[];
	lengthDependent: ChannelId[];
}

const memo = new WeakMap<ViewModel, Derived>();

export function derive(vm: ViewModel): Derived {
	const hit = memo.get(vm);
	if (hit) return hit;
	const detail = new Map<ChannelId, ChannelDetail>(vm.channels.map((c) => [c.id, c]));
	const row = new Map<ChannelId, ChannelRow>(vm.ensemble.channels.map((c) => [c.id, c]));
	const hotspotsByChannel = new Map<ChannelId, number>();
	for (const h of vm.ensemble.hotspots.hotspots)
		hotspotsByChannel.set(h.channel, (hotspotsByChannel.get(h.channel) ?? 0) + 1);
	const cs = vm.paras.charStart;
	const paraOf = (pos: number): number => {
		let lo = 0;
		let hi = cs.length - 1;
		while (lo < hi) {
			const mid = (lo + hi + 1) >> 1;
			if ((cs[mid] as number) <= pos) lo = mid;
			else hi = mid - 1;
		}
		return lo;
	};
	const d: Derived = {
		detail,
		row,
		hotspotsByChannel,
		paraOf,
		withheld: vm.ensemble.hotspots.withheld.map((w) => w.id),
		lengthDependent: vm.ensemble.diagnostics.lengthDependent,
	};
	memo.set(vm, d);
	return d;
}

/** Hotspot entries whose stretch lies inside paragraphs [a, b): counted by where the entry starts. */
export function hotspotsInRange(hotspots: readonly Hotspot[], a: number, b: number): number {
	let n = 0;
	for (const h of hotspots) if (h.paraStart >= a && h.paraStart < b) n++;
	return n;
}

export const ED14_NOTE =
	'length-dependent until the channel priors are replaced by corpus values (DESIGN ED-14)';

const prefixMemo = new WeakMap<ViewModel, Uint32Array>();

/** Prefix sums of hotspot entries by the paragraph they start in: entries inside [a, b) = p[b] − p[a]. */
export function hotspotPrefix(vm: ViewModel): Uint32Array {
	const hit = prefixMemo.get(vm);
	if (hit) return hit;
	const n = vm.paras.count;
	const counts = new Uint32Array(n + 1);
	for (const h of vm.ensemble.hotspots.hotspots)
		if (h.paraStart < n) counts[h.paraStart + 1] = (counts[h.paraStart + 1] as number) + 1;
	for (let i = 1; i <= n; i++) counts[i] = (counts[i] as number) + (counts[i - 1] as number);
	prefixMemo.set(vm, counts);
	return counts;
}

export function hotspotsBetween(vm: ViewModel, a: number, b: number): number {
	const p = hotspotPrefix(vm);
	return (p[Math.min(b, vm.paras.count)] as number) - (p[Math.min(a, vm.paras.count)] as number);
}

/** "Chapter 4 · paragraphs 120–188" for a stretch [paraStart, paraEnd) (paragraph numbers are 1-based in the UI). */
export function stretchLabel(vm: ViewModel, paraStart: number, paraEnd: number): string {
	const n = vm.paras.count;
	if (n === 0) return '';
	const a = Math.min(paraStart, n - 1);
	const b = Math.min(Math.max(paraEnd - 1, a), n - 1);
	const ca = vm.paras.chapter[a] as number;
	const cb = vm.paras.chapter[b] as number;
	const name = (c: number) => (c < 0 ? '' : (vm.chapters[c]?.title ?? `Chapter ${c + 1}`));
	const where = ca < 0 ? '' : ca === cb ? name(ca) : `${name(ca)} – ${name(cb)}`;
	const paras = a === b ? `paragraph ${a + 1}` : `paragraphs ${a + 1}–${b + 1}`;
	return where ? `${where} · ${paras}` : paras;
}

/** The index of the first evidence span of a channel (CHANNEL_IDS index `ci`), or −1. */
export function firstSpanOf(vm: ViewModel, ci: number): number {
	const ch = vm.spans.channel;
	for (let i = 0; i < vm.spans.count; i++) if (ch[i] === ci) return i;
	return -1;
}
